import { useState } from 'react';
import { Button, Accordion, AccordionSummary, AccordionDetails, Typography, Card } from '@attentive-platform/stem-ui';
import { DASHBOARD_SECTION_ENUM, DASHBOARD_MENU_SECTIONS, USER_COMPONENTS } from '../Constants/Constants';
import { isBeamUser } from '../Utils/HelperFunctions';

import OldDashboard from './OldDashboard';

const ModernDashboard = () => {
  const beamUser = isBeamUser();
  // Show legacy dropdown interface for Beam users
  if (beamUser) {
    return <OldDashboard />;
  }
  // Show new sidebar interface for non-Beam users
  return <SidebarDashboard />;
};

const SidebarDashboard = () => {
  const [activeSection, setActiveSection] = useState(DASHBOARD_SECTION_ENUM.REGISTRATION);
  const [expandedPanels, setExpandedPanels] = useState(['ACCOUNTS', 'CONVERSION', 'DOWNLOADS']);

  const handleAccordionChange = panel => (event, isExpanded) => {
    if (isExpanded) {
      setExpandedPanels(prev => [...prev, panel]);
    } else {
      setExpandedPanels(prev => prev.filter(p => p !== panel));
    }
  };

  const handleSectionClick = sectionId => {
    setActiveSection(sectionId);
  };

  const getSectionLabel = sectionId => {
    for (const section of Object.values(DASHBOARD_MENU_SECTIONS)) {
      const item = section.items.find(item => item.id === sectionId);
      if (item) {
        return `${section.title}/${item.label}`;
      }
    }
    return 'Dashboard';
  };

  const renderContent = () => {
    // enterprise conversion, copy transfer, and update contract need full width and split layout
    if (activeSection === DASHBOARD_SECTION_ENUM.ENTERPRISE_CONVERSION) {
      const EnterpriseConversionComponent = USER_COMPONENTS[activeSection];
      return <EnterpriseConversionComponent showCategoryPricing totalUsers={0} />;
    }

    if (
      activeSection === DASHBOARD_SECTION_ENUM.COPY_TRANSFER ||
      activeSection === DASHBOARD_SECTION_ENUM.UPDATE_CONTRACT
    ) {
      const Component = USER_COMPONENTS[activeSection];
      return typeof Component === 'function' ? <Component /> : Component;
    }

    const Component = USER_COMPONENTS[activeSection];
    if (Component) {
      return typeof Component === 'function' ? <Component /> : Component;
    }

    return USER_COMPONENTS[DASHBOARD_SECTION_ENUM.REGISTRATION];
  };

  const renderAccordionSection = (sectionKey, section) => {
    const items = section.items;

    return (
      <Accordion
        key={sectionKey}
        expanded={expandedPanels.includes(sectionKey)}
        onChange={handleAccordionChange(sectionKey)}
        className='bg-transparent m-0 pt-2'>
        <AccordionSummary className='min-h-0 h-8 pl-6'>
          <Typography variant='h5-medium' className='text-green-600'>
            {section.title}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <nav className='flex flex-col gap-0 pl-6'>
            {items.map(item => {
              const isActive = activeSection === item.id;

              return (
                <Button
                  key={item.id}
                  variant='text'
                  fullWidth
                  onClick={() => handleSectionClick(item.id)}
                  className={`
                    justify-start text-left rounded-md w-full whitespace-normal
                    transition-all duration-200
                    ${isActive ? 'bg-green-200 hover:bg-green-200' : 'hover:bg-green-200'}
                  `}>
                  <Typography variant='body7' className={`${isActive ? 'text-green-500' : 'text-black'}`}>
                    {item.label}
                  </Typography>
                </Button>
              );
            })}
          </nav>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <div className='flex bg-gray-100 rounded-lg w-full shadow-sm min-h-screen pr-10'>
      <div className='flex gap-6 w-full'>
        {/* left sidebar */}
        <Card className='w-96 flex-shrink-0 rounded-2xl bg-[#E9F7EF] h-screen overflow-y-auto flex flex-col sticky top-0'>
          {renderAccordionSection('ACCOUNTS', DASHBOARD_MENU_SECTIONS.ACCOUNTS)}
          {renderAccordionSection('CONVERSION', DASHBOARD_MENU_SECTIONS.CONVERSION)}
          {renderAccordionSection('DOWNLOADS', DASHBOARD_MENU_SECTIONS.DOWNLOADS)}
        </Card>

        {/* content area handle separately for copy transfer, enterprise conversion, and update contract*/}
        {activeSection === DASHBOARD_SECTION_ENUM.COPY_TRANSFER ||
        activeSection === DASHBOARD_SECTION_ENUM.ENTERPRISE_CONVERSION ||
        activeSection === DASHBOARD_SECTION_ENUM.UPDATE_CONTRACT ? (
          <div className='flex-1 mt-3 pb-6'>{renderContent()}</div>
        ) : (
          <Card className='mt-3 rounded-2xl w-[38%] self-start'>
            <div className='border-b border-gray-200 px-6 pt-4 pb-0'>
              <Typography variant='h6-medium'>{getSectionLabel(activeSection)}</Typography>
            </div>
            <div className='p-4'>{renderContent()}</div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ModernDashboard;
